import React, {createContext, useContext, useMemo} from "react"
import {useLocation, useNavigate} from "react-router-dom"
import {DataContext} from "./DataContextProvider"
import {GlobalContext} from "../../anwendungskonfigurator/context-provider/GlobalContextProvider"
import TomClient from "../../anwendungskonfigurator/services/tomClient"
import {backendBaseUrl} from "../../anwendungskonfigurator/misc/utils"
import {errorParser} from "../../anwendungskonfigurator/misc/utils"
import {ApplicationActions} from "../../anwendungskonfigurator/actions/application-actions"
import {navigationActions} from "../actions/navigation-actions"
import {useTheme} from "@emotion/react"
import {KundeActions} from "../../anwendungskonfigurator/actions/kunde-actions"
import {
    LebensmeilensteineAction
} from "../../anwendungskonfigurator/actions/lebensmeilensteine-action"
import {
    VersorgungsuebersichtActions
} from "../../anwendungskonfigurator/actions/versorgungsuebersicht-actions"
import {SchiessplatzActions} from "../../anwendungskonfigurator/actions/schiessplatz-actions"
import {
    lebensitutationssteinAction
} from "../../anwendungskonfigurator/actions/lebensitutationsstein-action"
import {
    VersorgungslueckenVerlaufActions
} from "../../anwendungskonfigurator/actions/versorgungsluecken-verlauf"
import {GesamtanalyseActions} from "../../anwendungskonfigurator/actions/gesamtanalyse-actions"
import {TariferhoehungsActions} from "../../anwendungskonfigurator/actions/tariferhoehungs-actions"
import {WeltboardActions} from "../../anwendungskonfigurator/actions/weltboard-actions"
import {CockpitActions} from "../../anwendungskonfigurator/actions/cockpit-actions"
import {BedarfsCheckActions} from "../../anwendungskonfigurator/actions/bedarfs-check-actions"
import {siSchiessplatzActions} from "../../anwendungskonfigurator/actions/si-schiessplatz-actions"
import {FigmaPrototypeActions} from "../../anwendungskonfigurator/actions/figma-prototype-actions"
import {RiesterActions} from "../../anwendungskonfigurator/actions/riester-actions"
import {
    AltersvorsorgelueckeSchliesenActions
} from "../../anwendungskonfigurator/actions/altersvorsorge-luecke-schliesen-action"
import {CrossSiteActions} from "../../anwendungskonfigurator/actions/cross-site-actions"
import {
    HinterbliebenenStreckeActions
} from "../../anwendungskonfigurator/actions/hinterbliebenen-strecke-actions"
import {
    EinkommensschutzStreckeActions
} from "../../anwendungskonfigurator/actions/einkommensschutz-strecke-actions"
import {
    VorsorgeAssistentActions
} from "../../anwendungskonfigurator/actions/vorsorge-assistent-actions"
import {ContextAreaActions} from "../../anwendungskonfigurator/actions/context-area-actions"
import {UwpActions} from "../actions/uwp-actions/uwp-actions"
import {PartnerActions} from "../actions/partner-actions"
import {WuenscheUndZieleActions} from "../../anwendungskonfigurator/actions/wuensche-und-ziele"
import {AccordeonPageActions} from "../../anwendungskonfigurator/actions/accordeon-page-actions"
import {validate} from "../../anwendungskonfigurator/misc/validation/simple-validator"
import {
    ROOT_DIALOG_POINTER
} from "../../anwendungskonfigurator/components/RootComponents/RootDialog"

export const processAndSortArray = (data) => {
    const aggregatedData = data.reduce((acc, item) => {
        const id = item.id
        const weight = parseInt(item.weight)

        if (acc[id]) {
            acc[id].weight = (parseInt(acc[id].weight) + weight).toString()
        } else {
            acc[id] = {...item}
        }
        return acc
    }, {})

    const resultArray = Object.values(aggregatedData)
    resultArray.sort((a, b) => parseInt(b.weight) - parseInt(a.weight))

    return resultArray
}

export const ActionContext = createContext({
    triggerAction: () => {},
})

const ActionContextProvider = ({children}) => {
    const {getDataFromPointer, updateData, setToDefault} = useContext(DataContext)
    const theme = useTheme()
    const {openDialog, closeDialog, selectDialogContainer, akJson} =
        useContext(GlobalContext)
    const navigate = useNavigate()
    const location = useLocation()
    const uwpBackendBaseUrl = globalThis._env_.UWPPLUS_BACKEND_API_URL + "/api"
    const handleResponse =
        (updateData, pointer, mapperFunction = (x) => x) =>
        (response) => {
            if (response) {
                updateData(
                    mapperFunction(
                        response?._data ?? response,
                        response?._links,
                    ),
                    pointer,
                )
                if (response._links) {
                    updateData(
                        {...getDataFromPointer("/links"), ...response._links},
                        "/links",
                    )
                }
                if (response._embedded) {
                    updateData(response._embedded, "/embedded" + pointer)
                }
            }
            //Das wird wieder reinkommen. Verantwortliches Ticket: VW-1144. Folgeticket existiert.
            /*if (_.isArray(response?._messages)) {
                response._messages.map((message) =>
                    toastifySeverityMap[message?.severity]?.(message.text, {
                        toastId: message.text,
                    }),
                )
            }*/

            return Promise.resolve(response)
        }

    const getDataFromBackend = (hallink, pointer, mapperFunction) =>
        TomClient.callBackend(hallink).then(
            handleResponse(updateData, pointer, mapperFunction),
            errorParser(updateData, pointer),
        )

    const getDataFromBackendWithQuery = (hallink, pointer, queryObject) =>
        TomClient.callBackend(hallink, {query: queryObject}).then(
            handleResponse(updateData, pointer),
            errorParser(updateData, pointer),
        )

    const sendDataToBackend = (
        hallink,
        payload,
        pointer,
        mapperFunction,
        updateDatafunc = updateData,
    ) =>
        TomClient.callBackend(hallink, {body: payload}).then(
            handleResponse(updateDatafunc, pointer, mapperFunction),
            errorParser(updateDatafunc, pointer),
        )

    /**
     *
     * @param hallink string
     * @param file Arraybuffer
     * @param pointer string
     * @param responseMapper function
     */
    const sendFileToBackend = (hallink, file, pointer, responseMapper) =>
        TomClient.callBackendFile(hallink, {
            body: file,
            contentType: "application/octet-stream",
            accept: "application/json",
        })
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(
                handleResponse(updateData, pointer, responseMapper),
                errorParser(updateData, pointer),
            )

    const makeLoadCurrentLocationData =
        (baseUrl) => (pointer, mapperFunction) => {
            const selfLink = baseUrl + location.pathname
            if (selfLink.includes(":id")) {
                return Promise.reject()
            }
            return TomClient.callBackend({href: selfLink}).then(
                (response) => {
                    updateData({}, pointer)
                    handleResponse(updateData, pointer, mapperFunction)(response)
                    updateData(response._links, "/links")

                    return Promise.resolve(response)
                },
            )
        }

    const loadCurrentLocationData = makeLoadCurrentLocationData(backendBaseUrl)
    const loadCurrentLocationDataUwp =
        makeLoadCurrentLocationData(uwpBackendBaseUrl)

    const triggerAction = (actionTypes, payload) => {
        return Promise.all(
            actionTypes.split(",").map((actionType) => {
                if (!Actions[actionType]) {
                    throw new Error(
                        `Es ist keine Anweisung für diese Action definiert, schaue im ActionContextProvider nach. ${actionType}`,
                    )
                }
                try {
                    return Actions[actionType](payload)
                } catch (error) {
                    console.error(error, actionType)
                    throw new Error(
                        "Fehler beim Ausführen der Action. Siehe Konsole für Details.",
                    )
                } finally {
                    validate(getDataFromPointer, updateData)
                }
            }),
        )
    }

    const openRootDialog = (identifier) => {
        updateData(identifier, ROOT_DIALOG_POINTER)
    }

    const closeRootDialog = () => {
        updateData(null, ROOT_DIALOG_POINTER)
    }

    const actionProps = {
        openRootDialog,
        closeRootDialog,
        getDataFromPointer,
        updateData,
        navigate,
        selectDialogContainer,
        openDialog,
        closeDialog,
        getDataFromBackend,
        sendDataToBackend,
        loadCurrentLocationData,
        setToDefault,
        getDataFromBackendWithQuery,
        triggerAction,
        akJson,
        theme,
        sendFileToBackend,
    }

    const Actions = {
        ...ApplicationActions(actionProps),
        ...navigationActions(actionProps),
        ...KundeActions(actionProps),
        ...LebensmeilensteineAction(actionProps),
        ...VersorgungsuebersichtActions(actionProps),
        ...SchiessplatzActions(actionProps),
        ...lebensitutationssteinAction(actionProps),
        ...VersorgungslueckenVerlaufActions(actionProps),
        ...GesamtanalyseActions(actionProps),
        ...TariferhoehungsActions(actionProps),
        ...WeltboardActions(actionProps),
        ...CockpitActions(actionProps),
        ...BedarfsCheckActions(actionProps),
        ...siSchiessplatzActions(actionProps),
        ...FigmaPrototypeActions(actionProps),
        ...RiesterActions(actionProps),
        ...CrossSiteActions(actionProps),
        ...AltersvorsorgelueckeSchliesenActions(actionProps),
        ...HinterbliebenenStreckeActions(actionProps),
        ...EinkommensschutzStreckeActions(actionProps),
        ...VorsorgeAssistentActions(actionProps),
        ...ContextAreaActions(actionProps),
        ...AccordeonPageActions(actionProps),
        ...WuenscheUndZieleActions(actionProps),
        ...UwpActions({
            ...actionProps,
            loadCurrentLocationData: loadCurrentLocationDataUwp,
        }),
        // Eigener Namensraum (PARTNER_*), damit die beiden bestehenden KundeActions mit ihren
        // gleichlautenden Namen unberuehrt bleiben — siehe Kommentar in partner-actions.js.
        ...PartnerActions(actionProps),
    }

    const value = useMemo(
        () => ({
            sendDataToBackend,
            triggerAction,
        }),
        [sendDataToBackend, triggerAction],
    )

    return (
        <ActionContext.Provider value={value}>
            {children}
        </ActionContext.Provider>
    )
}

export default ActionContextProvider
